<template>
    <div>
      <div class="d-flex justify-content-between">
        <h1 class="page-header">{{ $t('bankProfile.pageTitle') }}</h1>
        <ts-breadcrumb
          :items="[
            { text: $t('home'), href: '/' },
            {
              text: $t('bankProfile.pageTitle'),
              active: true
            }
          ]"
        />
      </div>
      <ts-card>
        <ts-card-wrapper>
          <div class="d-flex justify-content-between">
            <ts-button outline color="info" @click.prevent="addNew">{{
              $t('add_new')
            }}</ts-button>
            <div class="d-flex">
              <ts-input
                v-model="search"
                :placeholder="$t('search')"
                style="width: 250px"
                class="inline-block"
              />
            </div>
          </div>
        </ts-card-wrapper>
        <Table
				  :columns="columns"
          :data="resources"
          size="small"
          :loading="loading"
          highlight-row
          stripe
          max-height="700"
        >
					<template slot-scope="{ row }" slot="bankLogo">
						<img
                class="ts-rounded ts-h-24 ts-w-24"
                v-lazy="{
                  src: row.bank_logo,
                  error: require('@/assets/upload.png'),
                  loading: require('@/assets/Rolling-1s-200px.svg'),
                  preLoad: 1.3,
                  attempt: 1
                }"
              />
          </template>
					<template slot-scope="{ row }" slot="accountNumber">
							<td  v-for="(a, index) in row.accounts" class="ts-px-2" style="border-bottom: none;">
								<div
									:key="index"
									class="ts-border ts-float-left ts-p-2 ts-rounded"
									style="width: fit-content"
								>
									<p class="ts-mb-0" v-if="a.prefix">
										<span>Prefix: </span>
										<span
											class="ts-text-green-500 ts-font-extrabold ts-tracking-wider"
											>{{ a.prefix }}</span
										>
									</p>
									<p class="ts-mb-0">
										<span>Account Number: </span>
										<span
											class="ts-text-blue-600 ts-font-extrabold ts-tracking-wider"
											>{{ a.account_number }}</span
										>
									</p>
									<p class="ts-mb-0">
										<span>Account Name: </span>
										<span class="ts-text-yellow-700">{{ a.account_name }}</span>
									</p>
								</div>
							</td>
          </template>
					<template slot-scope="{ row }" slot="action">
						<a
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary"
                @click.prevent="edit(row)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                placement="leftTop"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(row)"
              >
                <a href="#" :disabled="row._deleting">
                  <i
                    class="fas fa-circle-notch spin ml-2"
                    v-if="row._deleting"
                  ></i>
                  <i class="fa fa-trash ml-2 text-red" v-else></i>
                </a>
              </a-popconfirm>
          </template>
        </Table>
        <div class="d-flex justify-content-between align-items-center p-15">
          <ts-total-list v-model="pagination" />
          <ts-pagination
            v-model="pagination"
            @navigate="fetchData"
          ></ts-pagination>
        </div>
        <!-- form-action -->
        <a-modal
          v-if="showForm"
          v-model="showForm"
          :title="$t('bankProfile.pageTitle')"
          centered
          @cancel="clearEdit"
          :maskClosable="false"
          :zIndex="1020"
          :footer="null"
          width="650px"
        >
          <from-action @cancel="clearEdit" />
        </a-modal>
      </ts-card>
    </div>
  </template>
  
  <script>
  import { debounce } from 'lodash'
  import { mapState } from 'vuex'
  import FromAction from './form'
  
  export default {
    name: 'bankProfile',
    components: {
      FromAction
    },
    data () {
      return {
        loading: false,
        showForm: false
      }
    },
    computed: {
      ...mapState('corporate/bankProfile', ['resources', 'pagination']),
      search: {
        get () {
          return this.$store.state.corporate.bankProfile.search
        },
        set (newValue) {
          this.$store.commit('corporate/bankProfile/SET_SEARCH', newValue)
          this.$store.commit('corporate/bankProfile/RESET_CURRENT_PAGE')
        }
      },
      columns () {
        return [
          { 
						title: this.$t('bankProfile.logo'),
						slot: 'bankLogo' 
					},
          { 
						title: this.$t('bankProfile.bankName'),
						key: 'bank_name', 
					},
          { 
						title: this.$t('bankProfile.accountNumber'),
						slot: 'accountNumber',
						width: 1120
					},
          { 
						title: this.$t('bankProfile.description'),
						key: 'description' 
					},
					{
            title: this.$t('actions'),
          	key: 'action',
          	slot: 'action',
          	fixed: 'right',
          	align: 'center',
          	width: 90
          }
        ]
      }
    },
    methods: {
      fetchData (attributes) {
        this.loading = true
        this.$store
          .dispatch('corporate/bankProfile/fetch', { ...attributes })
          .then(response => {
            this.loading = false
          })
          .catch(error => {
            this.loading = false
            this.$toasted.error(error.message)
          })
      },
      addNew () {
        this.showForm = true
      },
      edit (record) {
        this.$store.commit('corporate/bankProfile/SET_EDIT_DATA', record)
        this.showForm = true
      },
      clearEdit () {
        this.$store.commit('corporate/bankProfile/SET_EDIT_DATA', {})
        this.showForm = false
        this.fetchData()
      },
      deleteRecord (record) {
        record._deleting = true
        this.$store
          .dispatch('corporate/bankProfile/destroy', record.bank_id)
          .then(response => {
            this.$toasted.success(response.message)
            this.fetchData()
          })
          .catch(error => {
            this.$toasted.error(error.message)
          })
          .finally(() => {
            record._deleting = false
          })
      }
    },
    watch: {
      search: debounce(function (search) {
        this.fetchData()
      }, 500)
    },
    beforeRouteEnter (to, from, next) {
      next(vm => {
        vm.fetchData()
      })
    },
    beforeRouteLeave (to, from, next) {
      this.$store.commit('corporate/bankProfile/RESET_STATE')
      next()
    }
  }
  </script>
  